/**
 * Contact schemas — client-side validation for /api/contacts/* endpoints.
 *
 * Server is authoritative (apps/api/src/routes/contacts.ts uses similar schema
 * with permissive phone string + libphonenumber server-side normalization).
 * Client-side schemas here are STRICTER for fast UX feedback:
 *   - phone is permissive (server normalizes via libphonenumber + user country)
 *   - email through emailSchema (lowercase, trimmed)
 *   - notes/names: char limits matching backend LIMITS
 *
 * On submit, client sends the input string AS-IS to backend; backend
 * re-parses with user's default country to get +E.164 canonical form.
 * Client-side digits-only output is just for preview / display.
 */

import { z } from "zod";

import { emailSchema, uuidSchema } from "./primitives";

// --- Limits (mirror apps/api/src/routes/contacts.ts) ---

export const CONTACT_LIMITS = {
  PHONE_E164_MAX: 20,
  NAME_MAX: 100,
  EMAIL_MAX: 254,
  NOTES_MAX: 1000,
  CUSTOM_KEY_MAX: 64,
  CUSTOM_VALUE_MAX: 500,
  CUSTOM_FIELDS_MAX: 20,
} as const;

// --- Helpers ---

/**
 * "Optional or empty" string — accepts "" (empty input from form) and
 * coerces it to null. Result type is `string | null | undefined`, which
 * is what backend expects for nullable fields.
 *
 * Why this shape: react-hook-form's defaultValues = "" (not undefined),
 * so a plain `.optional()` would treat empty input as a non-empty string
 * and trigger length-based errors on every empty optional field.
 */
const optionalTrimmedString = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .or(z.literal(""))
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

/**
 * Same pattern but for email — runs through emailSchema only when input
 * is non-empty.
 */
const optionalEmail = z
  .string()
  .trim()
  .or(z.literal(""))
  .transform((v) => (v === "" ? null : v))
  .pipe(
    z.union([
      z.null(),
      emailSchema,
    ]),
  )
  .nullable()
  .optional();

/**
 * Phone — permissive client-side. Accepts:
 *   "0722964387"     (RO national)
 *   "+40722964387"   (E.164)
 *   "40722964387"    (digits-only intl)
 *   "0040722964387"  (00-prefix intl)
 *
 * Length 6-20 chars covers all formats. Backend re-parses with the
 * user's default country (RO fallback) via libphonenumber and stores
 * canonical +E.164.
 *
 * We deliberately do NOT run libphonenumber client-side because it
 * needs country context per user (which lives server-side) and the
 * library adds ~150 KB to the bundle just for client validation we
 * already get from the server response.
 */
const contactPhoneSchema = z
  .string()
  .trim()
  .min(6, "phone_too_short")
  .max(20, "phone_too_long")
  .refine((v) => /^[+0-9\s\-()]+$/.test(v), { message: "phone_invalid_chars" });

// --- Create contact request ---
//
// What the user types in AddContactModal. Phone is permissive (raw
// string with allowed chars); backend normalizes to canonical +E.164.
// Names/email/notes are server-trimmed there too. Empty strings on
// optional fields are coerced to undefined so they don't trigger
// "*_too_short" validation errors on empty input.

export const createContactRequestSchema = z.object({
  phone: contactPhoneSchema,
  first_name: optionalTrimmedString(CONTACT_LIMITS.NAME_MAX),
  last_name: optionalTrimmedString(CONTACT_LIMITS.NAME_MAX),
  email: optionalEmail,
  notes: optionalTrimmedString(CONTACT_LIMITS.NOTES_MAX),
  custom: z.record(
    z.string().max(CONTACT_LIMITS.CUSTOM_KEY_MAX),
    z.string().max(CONTACT_LIMITS.CUSTOM_VALUE_MAX),
  ).optional(),
  source_id: uuidSchema.optional().nullable(),
});

export type CreateContactRequest = z.infer<typeof createContactRequestSchema>;
export type CreateContactInput = z.input<typeof createContactRequestSchema>;

// --- Update contact request ---
//
// Used in ContactDrawer (D.4.B.4). Same shape but everything optional
// (PATCH semantics) plus required version for optimistic concurrency.

export const updateContactRequestSchema = z.object({
  version: z.number().int().min(1),
  phone: contactPhoneSchema.optional(),
  first_name: optionalTrimmedString(CONTACT_LIMITS.NAME_MAX),
  last_name: optionalTrimmedString(CONTACT_LIMITS.NAME_MAX),
  email: optionalEmail,
  notes: optionalTrimmedString(CONTACT_LIMITS.NOTES_MAX),
  custom: z.record(
    z.string().max(CONTACT_LIMITS.CUSTOM_KEY_MAX),
    z.string().max(CONTACT_LIMITS.CUSTOM_VALUE_MAX),
  ).optional(),
});

export type UpdateContactRequest = z.infer<typeof updateContactRequestSchema>;
export type UpdateContactInput = z.input<typeof updateContactRequestSchema>;
